import { LbNotificationExpiry } from '@electron/shared/lb-notification.types';
import {
  buildSkipEntry,
  type MeetingAutoStartSkipReason,
} from '@electron/shared/meeting-debug.types';
import {
  getProviderFromSegmentProviderName,
  type MeetingSegmentEvent,
  type MeetingSegmentIdentity,
} from '@electron/shared/meetings.types';
import { config } from '@littlebird/sdk';
import { useCallback, useEffect, useRef } from 'react';

import { electronApis } from '@/features/electron-api';
import { meetingDebugStore } from '@/features/meeting-debug/meeting-debug.store';
import { logError } from '@/lib/errorLogging';

import { findActiveCalendarEvent } from './meetings.query';
import { getMeeting, meetingStore } from './meetings.state';

/** Reconciled on read against `meetingId`, so a new recording starts clean
 * without an effect that resets state when the id changes. */
type MeetingSegmentPromptState = {
  meetingId: string;
  /** Every room this recording is known to capture, learned from `joined`.
   * A recording captures a second one when segmentation re-attaches, and it
   * does not stop having captured a room it has left, so this only grows. */
  coveredIdentityKeys: Set<string>;
  promptedIdentityKeys: Set<string>;
  /** The prompt still standing for this recording, superseded by the next one.
   * Prompts are queued and live for two minutes, so an A→B→C run would
   * otherwise leave B actionable after the browser already moved on to C. */
  pendingNotificationId: string | undefined;
};

function getSegmentIdentityKey(identity: MeetingSegmentIdentity): string {
  return `${identity.providerName}:${identity.meetingKey}`;
}

/**
 * Prompts the user to move the running recording when the browser moves to a
 * different meeting room.
 *
 * Segment events never start or stop a recording on their own: app detection
 * and presence loss stay the only triggers for that. Only `switched` is
 * actionable — `joined` names the room the recording is already in, and `left`
 * only ever means segmentation detached.
 */
export function useMeetingSegmentSwitchPrompt() {
  const recording = meetingStore.recording;
  const recordingStatus = recording.status;
  const activeMeetingId =
    recordingStatus === 'idle' ? undefined : recording.meetingId;
  const disableNotification = config.meetings.disable_detection_notification;
  const autoStartOnDetection = config.meetings.auto_start_on_detection;

  const latestSettingsRef = useRef({
    disableNotification,
    autoStartOnDetection,
    recordingStatus,
    activeMeetingId,
  });
  latestSettingsRef.current = {
    disableNotification,
    autoStartOnDetection,
    recordingStatus,
    activeMeetingId,
  };

  const promptStateRef = useRef<MeetingSegmentPromptState | null>(null);
  const getPromptStateForMeeting = useCallback((meetingId: string) => {
    if (promptStateRef.current?.meetingId !== meetingId) {
      promptStateRef.current = {
        meetingId,
        coveredIdentityKeys: new Set(),
        promptedIdentityKeys: new Set(),
        pendingNotificationId: undefined,
      };
    }

    return promptStateRef.current;
  }, []);

  const eventQueueRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    /** `meetingId` is the recording the event arrived under. An event outliving
     * that recording belongs to no other one: attributing it to the successor
     * would name the successor as the prompt's `precedingMeetingId`, which is
     * the one id the start-side stale guard cannot reject. */
    const onSegmentEvent = async (
      event: MeetingSegmentEvent,
      meetingId: string
    ) => {
      const outlivedItsRecording = () =>
        latestSettingsRef.current.activeMeetingId !== meetingId;
      const skip = (reason: MeetingAutoStartSkipReason) => {
        if (event.kind !== 'switched') return;
        meetingDebugStore.record({
          ...buildSkipEntry('segment_prompt', reason, {
            appName: event.app.name,
          }),
          meetingId,
        });
      };
      if (outlivedItsRecording()) {
        skip('recording_changed');
        return;
      }

      const settings = latestSettingsRef.current;
      const promptState = getPromptStateForMeeting(meetingId);
      const identityKey = getSegmentIdentityKey(event.identity);

      if (event.kind === 'left') return;

      // Before the `recording` check below, so the room a session started in is
      // known even when `joined` lands while it is still `loading`.
      if (event.kind === 'joined') {
        promptState.coveredIdentityKeys.add(identityKey);
        return;
      }

      if (settings.recordingStatus !== 'recording') {
        skip('recording_changed');
        return;
      }
      if (settings.disableNotification) {
        skip('detection_notification_disabled');
        return;
      }

      // A declined switch followed by a move back: already in the recording.
      if (promptState.coveredIdentityKeys.has(identityKey)) {
        skip('already_in_this_room');
        return;
      }
      if (promptState.promptedIdentityKeys.has(identityKey)) {
        skip('already_prompted');
        return;
      }
      promptState.promptedIdentityKeys.add(identityKey);

      const notificationId = `meeting-segment-switch:${meetingId}:${identityKey}`;

      try {
        if (
          await electronApis.lbNotifications.hasNotification(notificationId)
        ) {
          skip('notification_already_shown');
          return;
        }
        if (outlivedItsRecording()) {
          skip('recording_changed');
          return;
        }

        const provider = getProviderFromSegmentProviderName(
          event.identity.providerName
        );
        const calendarEvent = findActiveCalendarEvent(
          event.app.bundleIdentifier,
          {
            excludeEventId: getMeeting(meetingId)?.event_id ?? undefined,
            identity: { provider, meetingKey: event.identity.meetingKey },
          }
        );

        const supersededNotificationId = promptState.pendingNotificationId;
        if (
          supersededNotificationId &&
          supersededNotificationId !== notificationId
        ) {
          promptState.pendingNotificationId = undefined;
          await electronApis.lbNotifications.dismissById(
            supersededNotificationId
          );
          if (outlivedItsRecording()) {
            skip('recording_changed');
            return;
          }
        }

        await electronApis.lbNotifications.sendNotification({
          id: notificationId,
          view: 'meeting-segment-switch',
          expiresAt: LbNotificationExpiry.alert(),
          delivery: settings.autoStartOnDetection ? 'expedited' : 'queued',
          payload: {
            precedingMeetingId: meetingId,
            appName: event.app.name,
            appBundleId: event.app.bundleIdentifier,
            provider,
            meetingKey: event.identity.meetingKey,
            eventId: calendarEvent?.id,
            eventTitle: calendarEvent?.summary,
            autoSwitchOnDetection: settings.autoStartOnDetection,
          },
          sound: 'ask',
        });

        promptState.pendingNotificationId = notificationId;
      } catch (error) {
        promptState.promptedIdentityKeys.delete(identityKey);
        logError(
          error,
          '[meeting-segment-switch] Failed to send switch notification'
        );
      }
    };

    return electronApis.meetings.listenForMeetingSegmentEvent((event) => {
      meetingDebugStore.record({
        kind: 'segment_event',
        summary: `Segment ${event.kind} (${event.app.name})`,
        fields: event,
      });
      const meetingId = latestSettingsRef.current.activeMeetingId;
      if (!meetingId) return;

      eventQueueRef.current = eventQueueRef.current
        .then(() => onSegmentEvent(event, meetingId))
        .catch(() => {});
    });
  }, [getPromptStateForMeeting]);
}
